import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Pause, SkipBack, SkipForward, Volume2, ExternalLink } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import { sessionService } from '../services/sessionService';
import { useAudio } from '../contexts/AudioContext';

interface TimelineViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  startSeconds: number;
  endSeconds?: number;
  sessionTitle?: string;
}

interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker?: string;
}

interface SpeakerLabels {
  [key: number]: string;
}

const TimelineViewerModal: React.FC<TimelineViewerModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  startSeconds,
  endSeconds,
  sessionTitle
}) => {
  const { isDarkMode } = useDarkMode();
  const { token } = useAuth();
  const { 
    loadAudio, 
    playSegment, 
    play, 
    pause, 
    seekTo,
    currentTime: audioCurrentTime,
    duration: audioDuration,
    isPlaying: audioIsPlaying,
    isLoading: audioIsLoading
  } = useAudio();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([]);
  const [sessionData, setSessionData] = useState<any>(null);
  const [speakerLabels, setSpeakerLabels] = useState<SpeakerLabels>({});
  const speakerSideAssignments = useRef<Map<string, 'left' | 'right'>>(new Map());

  // Load session data and audio
  useEffect(() => {
    if (!isOpen || !sessionId || !token) return;

    const loadSessionData = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        console.log('[TimelineViewerModal] Loading session:', sessionId);
        const data = await sessionService.getSession(sessionId, token);
        console.log('[TimelineViewerModal] Session data loaded:', data);
        setSessionData(data);

        // Extract transcript segments
        if (data.transcriptionData?.results?.utterances) {
          const segments = data.transcriptionData.results.utterances.map((utterance: any) => ({
            start: utterance.start,
            end: utterance.end,
            text: utterance.transcript,
            speaker: utterance.speaker !== undefined && utterance.speaker !== null 
              ? `Speaker ${utterance.speaker}` 
              : 'Unknown Speaker'
          }));
          console.log('[TimelineViewerModal] Extracted segments:', segments.length);
          setTranscriptSegments(segments);
        } else {
          console.warn('[TimelineViewerModal] No utterances found in transcription data');
          setTranscriptSegments([]);
        }

        // Set speaker labels if available
        if (data.speakerLabels) {
          setSpeakerLabels(data.speakerLabels);
        }

        // Load audio if available
        if (data.audioUrl) {
          console.log('[TimelineViewerModal] Loading audio from session');
          loadAudio(data.audioUrl);
          
          // Wait for audio to load, then seek and play
          setTimeout(() => {
            playSegment(startSeconds, endSeconds);
          }, 500);
        } else {
          console.warn('[TimelineViewerModal] No audio URL available for session');
        }
      } catch (err) {
        console.error('Failed to load session data:', err);
        setError('Failed to load session data');
      } finally {
        setIsLoading(false);
      }
    };

    loadSessionData();
  }, [isOpen, sessionId, token, loadAudio, playSegment, startSeconds, endSeconds]);

  // Cleanup audio when modal closes
  useEffect(() => {
    if (!isOpen && audioIsPlaying) {
      pause();
    }
  }, [isOpen, audioIsPlaying, pause]);


  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (audioIsPlaying) {
      pause();
    } else {
      play();
    }
  };

  const handleSeek = (newTime: number) => {
    seekTo(newTime);
  };

  const handleSkipBack = () => {
    const newTime = Math.max(0, audioCurrentTime - 10);
    handleSeek(newTime);
  };

  const handleSkipForward = () => {
    const newTime = Math.min(audioDuration, audioCurrentTime + 10);
    handleSeek(newTime);
  };

  const getCurrentSegment = () => {
    return transcriptSegments.find(segment => 
      audioCurrentTime >= segment.start && audioCurrentTime <= segment.end
    );
  };

  const getSegmentInRange = () => {
    console.log('[TimelineViewerModal] Getting segments in range:', { 
      startSeconds, 
      endSeconds, 
      totalSegments: transcriptSegments.length 
    });
    
    if (transcriptSegments.length === 0) {
      console.warn('[TimelineViewerModal] No transcript segments available');
      return [];
    }
    
    // If no end time specified, find segments around the start time
    if (!endSeconds) {
      // Look for segments within 5 seconds of the start time
      const nearbySegments = transcriptSegments.filter(segment => 
        Math.abs(segment.start - startSeconds) < 5
      );
      
      if (nearbySegments.length > 0) {
        return nearbySegments;
      }
      
      // If no nearby segments, find the closest segment
      const closestSegment = transcriptSegments.reduce((closest, segment) => {
        const closestDist = Math.abs(closest.start - startSeconds);
        const segmentDist = Math.abs(segment.start - startSeconds);
        return segmentDist < closestDist ? segment : closest;
      });
      
      return [closestSegment];
    }
    
    // If we have both start and end, find all segments that overlap with the range
    const overlappingSegments = transcriptSegments.filter(segment => {
      // Check if segment overlaps with our time range
      const segmentOverlaps = (
        (segment.start >= startSeconds && segment.start <= endSeconds) ||
        (segment.end >= startSeconds && segment.end <= endSeconds) ||
        (segment.start <= startSeconds && segment.end >= endSeconds)
      );
      return segmentOverlaps;
    });
    
    console.log('[TimelineViewerModal] Found overlapping segments:', overlappingSegments.length);
    
    // If no overlapping segments, find segments close to the range
    if (overlappingSegments.length === 0) {
      const tolerance = 5; // 5 seconds tolerance
      const nearbySegments = transcriptSegments.filter(segment => 
        (segment.end >= startSeconds - tolerance && segment.start <= endSeconds + tolerance)
      );
      
      console.log('[TimelineViewerModal] Found nearby segments with tolerance:', nearbySegments.length);
      return nearbySegments.length > 0 ? nearbySegments : [];
    }
    
    return overlappingSegments;
  };

  // Helper functions for WhatsApp-style UI
  const getSpeakerColor = (speaker: string, allSpeakers: string[]) => {
    const colors = [
      'bg-blue-500 dark:bg-blue-600 text-white',
      'bg-purple-500 dark:bg-purple-600 text-white', 
      'bg-green-500 dark:bg-green-600 text-white',
      'bg-red-500 dark:bg-red-600 text-white',
      'bg-yellow-500 dark:bg-yellow-600 text-black dark:text-white',
      'bg-pink-500 dark:bg-pink-600 text-white',
      'bg-indigo-500 dark:bg-indigo-600 text-white',
      'bg-teal-500 dark:bg-teal-600 text-white'
    ];
    
    const index = allSpeakers.indexOf(speaker);
    return colors[index % colors.length];
  };

  const getMessageSide = (speaker: string) => {
    // If we haven't assigned this speaker yet, assign them a side
    if (!speakerSideAssignments.current.has(speaker)) {
      // Count how many speakers are already assigned to each side
      let leftCount = 0;
      let rightCount = 0;
      speakerSideAssignments.current.forEach(side => {
        if (side === 'left') leftCount++;
        else rightCount++;
      });
      
      // Assign to the side with fewer speakers, or left if equal
      const newSide = leftCount <= rightCount ? 'left' : 'right';
      speakerSideAssignments.current.set(speaker, newSide);
    }
    
    return speakerSideAssignments.current.get(speaker) || 'left';
  };

  const formatTextWithParagraphs = (text: string) => {
    // Split by common paragraph indicators
    const paragraphs = text.split(/\n\n|\. (?=[A-Z])/g);
    return paragraphs.filter(p => p.trim()).map(p => p.trim());
  };

  const getAllSpeakers = () => {
    const speakers = new Set<string>();
    transcriptSegments.forEach(segment => {
      if (segment.speaker) speakers.add(segment.speaker);
    });
    return Array.from(speakers).sort();
  };

  if (!isOpen) return null;

  const currentSegment = getCurrentSegment();
  const rangeSegments = getSegmentInRange();
  const playbackRange = endSeconds ? `${formatTime(startSeconds)} - ${formatTime(endSeconds)}` : formatTime(startSeconds);
  
  console.log('[TimelineViewerModal] Render state:', {
    rangeSegments,
    isArray: Array.isArray(rangeSegments),
    segmentCount: Array.isArray(rangeSegments) ? rangeSegments.length : 'not array'
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`
        w-full max-w-4xl mx-4 rounded-lg shadow-xl
        ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
      `}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div>
            <h2 className={`text-lg font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Timeline Viewer
            </h2>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {sessionTitle || `Session ${sessionId.substring(0, 8)}...`} • {playbackRange}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                window.open(`/session/${sessionId}`, '_blank');
              }}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
              title="Open full session in new tab"
            >
              <ExternalLink size={18} />
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
            </div>
          ) : (
            <>

              {/* Player Controls */}
              <div className={`p-4 rounded-lg mb-6 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className={`w-full h-2 rounded-full ${
                    isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                  }`}>
                    <div 
                      className="h-full bg-blue-600 rounded-full transition-all duration-150"
                      style={{ width: `${audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-500">
                    <span>{formatTime(audioCurrentTime)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={handleSkipBack}
                    className={`p-2 rounded-full transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-gray-600 text-gray-300' 
                        : 'hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <SkipBack size={20} />
                  </button>

                  <button
                    onClick={handlePlayPause}
                    className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                  >
                    {audioIsPlaying ? <Pause size={24} /> : <Play size={24} />}
                  </button>

                  <button
                    onClick={handleSkipForward}
                    className={`p-2 rounded-full transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-gray-600 text-gray-300' 
                        : 'hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <SkipForward size={20} />
                  </button>

                  <div className="flex items-center gap-2 ml-4">
                    <Volume2 size={16} className="text-gray-500" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={1}
                      onChange={(e) => {
                        // Volume control would need to be added to the audio context
                        // For now, this is a placeholder
                        console.log('Volume control not yet implemented:', e.target.value);
                      }}
                      className="w-20"
                    />
                  </div>
                </div>
              </div>

              {/* TTML Metadata Display */}
              <div className={`rounded-lg p-4 ${
                isDarkMode ? 'bg-gray-750' : 'bg-gray-50'
              }`}>
                <h3 className={`text-sm font-medium mb-3 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Segment Information
                </h3>
                
                <div className="space-y-3">
                  <div className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <p className="mb-2">
                      <strong>Time Range:</strong> {playbackRange}
                    </p>
                    {endSeconds && (
                      <p>
                        <strong>Duration:</strong> {formatTime(endSeconds - startSeconds)}
                      </p>
                    )}
                  </div>
                  
                  <div className={`text-sm ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    <p className="italic">
                      Audio player will seek to this time range. Visit the Transcript page to view the full transcript.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelineViewerModal;